/* eslint-disable no-use-before-define */

import { Button, Card, Checkboxes } from 'nhsuk-react-components';
import { useState } from 'react';
import type { DateInputChangeEvent, DateInputValue } from 'nhsuk-react-components';
import { v4 as uuidv4 } from 'uuid';
import useCampaign from '../../hooks/useCampaign';
import { useForm } from '../../hooks/useForm';
import { Iteration } from '../../schemas/Iteration.schema';
import {
  validStringCharacterMessage,
  validStringCharacters,
} from '../../schemas/shared-validators/regex';
import {
  LabelDate,
  labelToYYYYMMDD,
  validateDateLengths,
  YYYYMMDDToUnixDate,
} from '../../utils/formattingUtils';
import { CohortRulesErrors } from './CohortRulesError';
import { CohortsSection } from './CohortsSection';
import { ITERATION_DETAILS, SECTIONS } from './Constants';
import IterationDetailsSection from './IterationDetailsSection';
import IterationName from './IterationName';
import IterationRunDate from './IterationRunDate';
import { RoutingPlanIdsSection } from './RoutingPlanIdsSection';
import { RulesSection } from './RulesSection';
import { useCheckboxes } from './hooks/useCheckboxes';
import useRulesData from './hooks/useRulesData';

import './Checkboxes.scss';

interface CopyOptionsFormProps {
    iteration: Iteration;
    onSuccessfulSubmission(iteration: Iteration): void;
    isPending?: boolean;
}

function CopyOptionsForm({
  iteration,
  onSuccessfulSubmission,
  isPending = false,
}: CopyOptionsFormProps) {
  const { campaign } = useCampaign();

  const {
    formValues,
    errors,
    clearErrorsForField,
    handleChange,
    handleSubmit,
  } = useForm<Iteration>({
    initialValues: { Name: '' },
    validate: (values) => validate(values),
    onSubmit: () => onSubmit(),
  });

  const {
    selectAll,
    checkedItems,
    sectionCheckboxes,
    setSectionCheckboxes,
    updateCheckboxItem,
    updateAllCheckboxItems,
    updateSectionCheckboxItem,
  } = useCheckboxes();

  const [filterRules, suppressionRules, routingRules] = useRulesData(
    iteration,
    checkedItems[SECTIONS.FILTER_RULES],
    checkedItems[SECTIONS.SUPPRESSION_RULES],
    checkedItems[SECTIONS.ROUTING_RULES],
  );

  const [dates, setDates] = useState<{ IterationDate: LabelDate }>({
    IterationDate: {
      day: '',
      month: '',
      year: '',
    },
  });

  const iterationName = iteration?.Name || 'Unknown';
  const cohortsDisabled = iteration?.IterationCohorts?.length === 0;

  const handleSelectAllChange = () => {
    const newCheckedState = !selectAll;

    updateAllCheckboxItems(!selectAll);

    setSectionCheckboxes({
      [SECTIONS.ITERATION_DETAILS]: newCheckedState,
      [SECTIONS.COHORTS]: newCheckedState && !cohortsDisabled,
      [SECTIONS.ROUTING_PLAN_IDS]:
                newCheckedState && iteration.DefaultCommsRouting !== '',
      [SECTIONS.FILTER_RULES]: newCheckedState && !filterRules.disabled,
      [SECTIONS.SUPPRESSION_RULES]:
                newCheckedState && !suppressionRules.disabled,
      [SECTIONS.ROUTING_RULES]: newCheckedState && !routingRules.disabled,
    });
  };

  const handleSectionCheckboxChange = (sectionId: string) => {
    updateSectionCheckboxItem(sectionId);
  };

  const handleCheckboxChange = (sectionKey: string, id: string) => {
    updateCheckboxItem(sectionKey, id);
  };

  const handleDateChange = (
    e: DateInputChangeEvent,
    Name: 'IterationDate',
  ) => {
    const { day = '', month = '', year = '' } = (e.target.value || {}) as Partial<DateInputValue>;
    const typedValue = { day, month, year };
    clearErrorsForField(Name);

    const errorMessage = validateDateLengths(typedValue);

    if (errorMessage) {
      errors.IterationDate = errorMessage;
    }

    setDates({
      ...dates,
      IterationDate: typedValue,
    });
  };

  const validate = (values: Partial<Iteration>) => {
    // eslint-disable-next-line no-shadow
    const errors: Record<string, string> = {};

    if (!values.Name?.trim()) {
      errors.Name = 'Iteration name is required';
    } else {
      const isUnique = !campaign.CampaignConfig?.Iterations?.some(
        (item) => item.Name?.trim() === values.Name?.trim(),
      );

      if (!isUnique) {
        errors.Name = 'Iteration name must be unique';
      } else if (!values.Name.match(validStringCharacters)) {
        errors.Name = validStringCharacterMessage;
      }
    }

    if (
      !dates.IterationDate?.day
            || !dates.IterationDate?.month
            || !dates.IterationDate?.year
    ) {
      errors.IterationDate = 'Iteration run date is required';
    } else {
      const runDate = labelToYYYYMMDD(dates.IterationDate);
      const unixIterationDate = YYYYMMDDToUnixDate(runDate);
      const unixCampaignStartDate = YYYYMMDDToUnixDate(
        campaign.CampaignConfig.StartDate || '',
      );
      const unixCampaignEndDate = YYYYMMDDToUnixDate(
        campaign.CampaignConfig.EndDate || '',
      );

      if (
        unixIterationDate < unixCampaignStartDate
                || unixIterationDate > unixCampaignEndDate
      ) {
        errors.IterationDate = 'Iteration run date must be within the campaign date range';
      } else {
        const errorMessage = validateDateLengths(dates.IterationDate);

        if (errorMessage) {
          errors.IterationDate = errorMessage;
        }
      }
    }

    const allCohorts = iteration.IterationCohorts || [];
    const excludedCohorts = allCohorts.filter(
      (cohort) => !checkedItems[SECTIONS.COHORTS]?.[cohort.CohortLabel || ''],
    );

    const rulesToAdd = [
      ...filterRules.rules
        .filter((rule) => rule.checked)
        .map((rule) => rule.iterationRule),
      ...suppressionRules.rules
        .filter((rule) => rule.checked)
        .map((rule) => rule.iterationRule),
    ];

    const invalidRules = rulesToAdd.filter((rule) => excludedCohorts.some(
      (cohort) => cohort.CohortLabel === rule.CohortLabel,
    ));

    if (invalidRules.length > 0) {
      errors.CohortRules = invalidRules
        .map((rule) => rule.Name)
        .join(', ');
    }

    return errors;
  };

  const onSubmit = () => {
    const newIterationId = uuidv4();
    const runDate = labelToYYYYMMDD(dates.IterationDate);

    const rulesToAdd = [
      ...filterRules.rules
        .filter((rule) => rule.checked)
        .map((rule) => rule.iterationRule),
      ...suppressionRules.rules
        .filter((rule) => rule.checked)
        .map((rule) => rule.iterationRule),
      ...routingRules.rules
        .filter((rule) => rule.checked)
        .map((rule) => rule.iterationRule),
    ];

    const rulesToAddWithAndClauses = rulesToAdd.flatMap((rule) => {
      if (rule.AndClauses && Array.isArray(rule.AndClauses)) {
        return [
          { ...rule, ID: uuidv4() },
          ...rule.AndClauses.map((andClause) => ({ ...andClause, ID: uuidv4() })),
        ];
      }
      return [{ ...rule, ID: uuidv4() }];
    });

    const cohortsToAdd = iteration.IterationCohorts?.filter(
      (cohort) => checkedItems[SECTIONS.COHORTS]?.[cohort.CohortLabel || ''],
    ) || [];

    const routingPlanIdsToAdd = Object.keys(
      checkedItems[SECTIONS.ROUTING_PLAN_IDS],
    ).filter((key) => checkedItems[SECTIONS.ROUTING_PLAN_IDS][key]);

    const newIteration : Iteration = {
      Version: 1,
      ID: newIterationId,
      IterationNumber: (campaign.CampaignConfig.Iterations?.length ?? 0) + 1,
      Name: formValues.Name,
      IterationDate: runDate,
      CommsType: checkedItems[SECTIONS.ITERATION_DETAILS][
        ITERATION_DETAILS.COMMUNICATION
      ]
        ? iteration.CommsType
        : 'I',
      ApprovalMinimum: iteration.ApprovalMinimum,
      ApprovalMaximum: iteration.ApprovalMaximum,
      Type: iteration.Type,
      IterationCohorts: cohortsToAdd,
      DefaultCommsRouting: routingPlanIdsToAdd.join('|'),
      IterationRules: rulesToAddWithAndClauses,
    };

    onSuccessfulSubmission(newIteration);
  };

  return (
    <>
      <Card>
        <form>
          <h3>
            Copy of: ‘
            {iterationName}
            ’
          </h3>

          <IterationName
            value={formValues.Name}
            onChange={handleChange}
            error={errors.Name}
          />

          <IterationRunDate
            value={dates.IterationDate}
            onChange={(e) => handleDateChange(e, 'IterationDate')}
            error={errors.IterationDate}
          />

          <h4>Iteration options</h4>
          <p>
            Click on the arrows next to the tick boxes to view more
            information.
          </p>

          <Checkboxes>
            <Checkboxes.Item
              id="select-all"
              data-testid="select-all"
              checked={selectAll}
              onChange={handleSelectAllChange}
            >
              <div className="nhsuk-u-font-weight-bold">
                <a>Select all</a>
              </div>
            </Checkboxes.Item>

            <p>Review the details carefully if you ‘Select all’.</p>

            <IterationDetailsSection
              iteration={iteration}
              checkedItems={
                                checkedItems[SECTIONS.ITERATION_DETAILS]
                            }
              sectionChecked={
                                sectionCheckboxes[SECTIONS.ITERATION_DETAILS]
                            }
              onSectionChange={handleSectionCheckboxChange}
              handleCheckboxChange={handleCheckboxChange}
            />

            <CohortsSection
              cohorts={iteration.IterationCohorts || []}
              checkedItems={checkedItems[SECTIONS.COHORTS]}
              sectionChecked={sectionCheckboxes[SECTIONS.COHORTS]}
              onSectionChange={handleSectionCheckboxChange}
              handleCheckboxChange={handleCheckboxChange}
            />

            <RoutingPlanIdsSection
              routingIds={iteration.DefaultCommsRouting || ''}
              checkedItems={
                                checkedItems[SECTIONS.ROUTING_PLAN_IDS]
                            }
              sectionChecked={
                                sectionCheckboxes[SECTIONS.ROUTING_PLAN_IDS]
                            }
              onSectionChange={handleSectionCheckboxChange}
              handleCheckboxChange={handleCheckboxChange}
            />

            <RulesSection
              {...filterRules}
              sectionCheckboxes={sectionCheckboxes}
              handleSectionCheckboxChange={
                                handleSectionCheckboxChange
                            }
              checkedItems={checkedItems[SECTIONS.FILTER_RULES]}
              handleCheckboxChange={handleCheckboxChange}
            />

            <RulesSection
              {...suppressionRules}
              sectionCheckboxes={sectionCheckboxes}
              handleSectionCheckboxChange={
                                handleSectionCheckboxChange
                            }
              checkedItems={
                                checkedItems[SECTIONS.SUPPRESSION_RULES]
                            }
              handleCheckboxChange={handleCheckboxChange}
            />

            <RulesSection
              {...routingRules}
              sectionCheckboxes={sectionCheckboxes}
              handleSectionCheckboxChange={
                                handleSectionCheckboxChange
                            }
              checkedItems={checkedItems[SECTIONS.ROUTING_RULES]}
              handleCheckboxChange={handleCheckboxChange}
            />
          </Checkboxes>

          {errors.CohortRules && (
            <CohortRulesErrors errors={errors.CohortRules} />
          )}
        </form>
      </Card>
      <Button onClick={handleSubmit} disabled={isPending}>
        {isPending ? 'Updating...' : 'Save and continue'}
      </Button>
    </>
  );
}

export default CopyOptionsForm;
